import { MeshGradient, buildSeededMeshConfig, useImageLoad } from '@backrs_ui/common-ui';

import { Box, Skeleton, type SxProps, type Theme } from '@mui/material';

type ProfileBannerProps = Readonly<{
  bannerImageUrl?: string | null;
  // Gradient fallback is deterministic per seed, so each user gets a stable, unique banner.
  seed: string | number;
  id?: string;
  sx?: SxProps<Theme>;
}>;

export const ProfileBanner = ({ bannerImageUrl, seed, id, sx }: ProfileBannerProps) => {
  const { imageLoaded, imageError, imageProps } = useImageLoad(bannerImageUrl ?? undefined);

  const showGradient = !bannerImageUrl || imageError;

  return (
    <Box id={id} sx={[containerSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}>
      {showGradient ? (
        <MeshGradient config={buildSeededMeshConfig(seed)} />
      ) : (
        <>
          {!imageLoaded && <Skeleton variant="rectangular" animation="wave" sx={fillSx} />}
          <Box
            component="img"
            src={bannerImageUrl}
            alt=""
            sx={imageSx(imageLoaded)}
            {...imageProps}
          />
        </>
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const fillSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

const imageSx =
  (loaded: boolean): SxProps<Theme> =>
  () => ({
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: loaded ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
  });
